import { useState } from "react";
import { toast } from "sonner";
import { submitHelpRequest } from "../lib/api";
import { ShieldCheck, Loader2 } from "lucide-react";

const CATEGORIES = [
  "Food", "Clothing", "School Supplies", "Haircuts", "Transportation",
  "Utility Assistance", "Rent Assistance", "Tutoring", "Mental Health",
  "Counseling", "Family Crisis", "Emergency Needs", "Prayer Request",
];

const inputCls = "w-full rounded-md border border-black/15 bg-white px-4 py-3 outline-none focus:border-[#6B21A8]";

export default function RequestHelp() {
  const [f, setF] = useState({
    requester_name: "", requester_email: "", requester_phone: "",
    categories: [], urgency: "normal", details: "",
    emergency_contact: "", confidential: true,
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const toggleCat = (v) => setF((s) => ({ ...s, categories: s.categories.includes(v) ? s.categories.filter((x) => x !== v) : [...s.categories, v] }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.categories.length === 0) return toast.error("Pick at least one category.");
    if (!f.details.trim()) return toast.error("Please describe how we can help.");
    setLoading(true);
    try {
      await submitHelpRequest(f);
      toast.success("We hear you. A team member will reach out privately within 24 hours.");
      setF({ requester_name: "", requester_email: "", requester_phone: "", categories: [], urgency: "normal", details: "", emergency_contact: "", confidential: true });
    } catch { toast.error("Something went wrong. Please try again or call us directly."); } finally { setLoading(false); }
  };

  return (
    <>
      <section className="bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="noise" />
        <div className="container-x relative py-20 md:py-24">
          <span className="overline text-[#F5B90A]">Request Help</span>
          <h1 className="mt-4 font-display text-5xl md:text-6xl font-bold tracking-tight leading-none">You're not alone.<br /><span className="italic text-[#F5B90A]">We got you.</span></h1>
          <p className="mt-6 max-w-2xl text-white/80 text-lg">
            Whatever you're facing — food, clothing, mental health, family crisis — reach out. Your request is confidential and routed directly to our care team.
          </p>
          <div className="mt-6 flex items-center gap-3 text-sm text-white/70">
            <ShieldCheck className="h-5 w-5 text-[#F5B90A]" /> Confidential · No cost · Response within 24 hours
          </div>
        </div>
      </section>

      <section className="bg-[#FDFBF7]">
        <div className="container-x py-16 max-w-3xl">
          <form onSubmit={submit} className="space-y-6 bg-white border border-black/10 p-8 md:p-10" data-testid="help-form">
            <div>
              <p className="text-sm font-medium text-black/80 mb-3">What kind of help do you need? <span className="text-[#6B21A8]">*</span></p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="help-cats">
                {CATEGORIES.map((c) => (
                  <label key={c} className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer text-sm ${f.categories.includes(c) ? "border-[#6B21A8] bg-[#6B21A8]/5" : "border-black/15"}`}>
                    <input type="checkbox" checked={f.categories.includes(c)} onChange={() => toggleCat(c)} className="h-4 w-4 rounded border-black/30 text-[#6B21A8]" data-testid={`help-cat-${c.replace(/\s+/g, "-").toLowerCase()}`} />
                    {c}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-black/80 mb-3">Urgency</p>
              <div className="flex flex-wrap gap-2" data-testid="help-urgency">
                {[["normal", "Normal"], ["urgent", "Urgent (need within a week)"], ["emergency", "Emergency (need today)"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setF({ ...f, urgency: v })} data-testid={`help-urgency-${v}`}
                    className={`rounded-full border px-4 py-1.5 text-sm ${f.urgency === v ? "border-[#6B21A8] bg-[#6B21A8] text-white" : "border-black/15"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="block text-sm font-medium text-black/80 mb-2">Tell us more <span className="text-[#6B21A8]">*</span></span>
              <textarea required rows="5" className={inputCls} value={f.details} onChange={set("details")} data-testid="help-details" placeholder="Share as much or as little as you're comfortable with." />
            </label>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="block text-sm font-medium text-black/80 mb-2">Your name</span>
                <input className={inputCls} value={f.requester_name} onChange={set("requester_name")} data-testid="help-name" />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-black/80 mb-2">Phone</span>
                <input className={inputCls} value={f.requester_phone} onChange={set("requester_phone")} data-testid="help-phone" />
              </label>
            </div>
            <label className="block">
              <span className="block text-sm font-medium text-black/80 mb-2">Email</span>
              <input type="email" className={inputCls} value={f.requester_email} onChange={set("requester_email")} data-testid="help-email" />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-black/80 mb-2">Emergency contact (name & phone)</span>
              <input className={inputCls} value={f.emergency_contact} onChange={set("emergency_contact")} data-testid="help-emergency" />
            </label>

            <button type="submit" disabled={loading} data-testid="help-submit" className="btn-primary w-full">
              {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>) : "Submit request"}
            </button>
            <p className="text-xs text-black/50 text-center">By submitting, you consent to a Change Center team member contacting you privately.</p>
          </form>
        </div>
      </section>
    </>
  );
}
